import { ICellRendererParams, RowNode } from '@ag-grid-community/core';
import { Button, Icon, IconButton, Status } from '@assembly-js/design-system';
import { useMediaQuery, useTheme } from '@material-ui/core';

import { ServicePriceActionRenderer } from 'src/routes/payments/services/components/ServicePriceActionRenderer';
import { ProductPriceTableRowModal } from 'src/routes/payments/services/types';
import { LineItem } from 'src/services/api/invoicesApi';

interface ExtendedCellRendererParams extends ICellRendererParams {
  handleSave: (node: RowNode) => void;
  handleDiscard: (node: RowNode) => void;
  handleClickRemove: (data: ProductPriceTableRowModal) => void;
  isDeleteDisabled: (data: ProductPriceTableRowModal) => boolean;
  isSavePriceLoading: boolean;
  isSmallViewPort: boolean;
  priceError: string;
  lineItems: LineItem[];
  mode: string;
  selectedPriceId: string;
  onSetPriceId: (priceId: string) => void;
  selectedInterval: string;
}

export const PriceActionsRenderer = (params: ExtendedCellRendererParams) => {
  const isNewRow = params.data.isNew;
  const theme = useTheme();
  const isExtraSmallViewPort = useMediaQuery(theme.breakpoints.down('xs'));
  if (isNewRow) {
    const isSaveDisabled =
      params.data.amount <= 0 || !params.data.interval || !!params.priceError;
    return (
      <div className="flex justify-end items-center gap-2">
        {isExtraSmallViewPort ? (
          <>
            <IconButton
              variant="secondary"
              label="Discard"
              onClick={() => params.handleDiscard(params.node)}
              icon="Close"
            />
            <IconButton
              variant="primary"
              label="Save"
              icon="Check"
              onClick={() => params.handleSave(params.node)}
              disabled={isSaveDisabled}
            />
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              label="Discard"
              prefixIcon="Close"
              onClick={() => params.handleDiscard(params.node)}
              disabled={params.isSavePriceLoading}
              className="[&>svg]:box-content"
            />
            <Button
              variant="primary"
              label="Save"
              prefixIcon="Check"
              disabled={isSaveDisabled}
              onClick={() => params.handleSave(params.node)}
              loading={params.isSavePriceLoading}
              className="[&>svg]:box-content"
            />
          </>
        )}
      </div>
    );
  } else {
    const {
      mode,
      selectedInterval,
      selectedPriceId,
      lineItems,
      data,
      onSetPriceId,
      handleClickRemove,
    } = params;
    const isDeleteDisabled = params.isDeleteDisabled(data);
    const isPriceUsed = lineItems.some((li) => li?.priceId === params.data.id);
    const showCurrentPriceBadge = selectedPriceId === params.data.id;
    // Updated logic for enabling "Set as current price"
    let isEnabledSetCurrentPriceAction = false;

    if (mode === 'OneTimeStore') {
      // Store One-time purchases: enable only when no interval is selected AND the price is not already used.
      isEnabledSetCurrentPriceAction = !data.interval && !isPriceUsed;
    } else if (mode === 'subscriptionsStore') {
      // Store Plans: enable only when the interval exists AND the price is not already used.
      isEnabledSetCurrentPriceAction = data.interval && !isPriceUsed;
    } else if (mode === 'All') {
      // PaymentLink : allow setting the current price for all type of prices.
      isEnabledSetCurrentPriceAction = true;
    } else {
      // If more than one line item exists → interval must match the selected interval.
      // If only one line item exists → always allow.
      isEnabledSetCurrentPriceAction =
        lineItems.length > 1 ? data.interval === selectedInterval : true;
    }

    return (
      <div className="flex items-center gap-2">
        {showCurrentPriceBadge && (
          <Status label="Current price" status="neutral" />
        )}
        <ServicePriceActionRenderer
          isDeleteDisabled={isDeleteDisabled}
          // We disallow price deletion if it's been used in a product that's already part of any of the billing surface (invoice/subscription/link/store)
          // showCurrentPriceBadge is a state that represent the selection of price for line item in the table. When true, shouldn't be deleted
          isPriceUsed={isPriceUsed || !!showCurrentPriceBadge}
          mode={params.mode}
          handleClickRemove={() => handleClickRemove(data)}
          data={data}
          entityName={'price'}
          {...(!showCurrentPriceBadge
            ? {
                onCurrentPriceAction: {
                  isDisabled: !isEnabledSetCurrentPriceAction,
                  name: 'Set as current price',
                  onClick: () => onSetPriceId(data.id),
                  icon: <Icon name="Check" icon="Check" className="h-3 w-3" />,
                },
              }
            : {})}
        />
      </div>
    );
  }
};
